// v0.4.52 — RateVerifyPanel: one-click TEDB-vs-bootstrap audit.
//
// Mounted inside the Settings → Rates tab. ADMIN-only (the underlying
// endpoint also enforces; this hides the affordance for OPERATOR).
//
// Click → POST /api/admin/rates/verify-against-tedb → renders the
// per-form/per-category table inline with green ticks (match) and
// amber chips (drift). Each amber row deep-links to the existing
// /api/admin/rates/acks workflow so operators can resolve drift
// without leaving the page.
//
// HTTP semantics from the backend:
//   200 → no discrepancies, no backdated acks
//   202 → drift detected OR backdated rate hit a frozen filing
//   503 → TEDB itself is unreachable
import React, { useCallback, useEffect, useState } from "react";
import { verifyRatesAgainstTedb, fetchDriftRuns } from "../lib/api";
import { useTranslation } from "react-i18next";

export const RateVerifyPanel = ({ isAdmin }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  // v0.4.53 — Fetch the latest drift run so the badge surfaces
  // continuous-confidence state without requiring a manual click.
  const loadLastRun = useCallback(async () => {
    const r = await fetchDriftRuns(1);
    if (r.ok) setLastRun(r.body?.latest || null);
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      const r = await fetchDriftRuns(1);
      if (!cancelled && r.ok) setLastRun(r.body?.latest || null);
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const run = async () => {
    setBusy(true);
    setErr(null);
    setResult(null);
    const r = await verifyRatesAgainstTedb();
    setBusy(false);
    if (r.status === 0 || r.status === 503) {
      setErr(r.error || "TEDB unreachable");
      // Even on failure, the worker persisted a TEDB_UNREACHABLE
      // row — refresh the badge so the operator sees the latest.
      loadLastRun();
      return;
    }
    setResult(r);
    loadLastRun();
  };

  const total = result?.body?.total_discrepancies ?? 0;
  const pendingAcks = result?.body?.tedb_fetch?.pending_acks_created ?? 0;
  const headerCls = total > 0 || pendingAcks > 0
    ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300";

  return (
    <div
      data-testid="rate-verify-panel"
      className="mt-4 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 rounded-xl p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300">
            {t("ui.settings.rate_verify.title", { defaultValue: "Verify against TEDB" })}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">
            {t("ui.settings.rate_verify.intro_before", { defaultValue: "Runs the read-only TEDB watchdog: fetches current EU rates and diffs them against both rate stores (" })}<code className="text-slate-600 dark:text-slate-300 px-1">eu_vat_rates.json</code>{t("ui.settings.rate_verify.intro_mid", { defaultValue: "for OSS +" })}
            <code className="text-slate-600 dark:text-slate-300 px-1">vat_rate_history</code>{/* i18n-ignore: code identifier, not copy */}
            {t("ui.settings.rate_verify.intro_after", { defaultValue: "for filing forms). Surfaces drift only — never writes a rate row." })}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <LastDriftBadge run={lastRun} />
          <button
            type="button"
            data-testid="rate-verify-button"
            onClick={run}
            disabled={busy}
            className={`text-xs px-4 py-2 rounded-xl ${
              busy ? "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-wait"
                  : "bg-teal-600 hover:bg-teal-700 text-white font-bold"
            }`}
          >
            {busy ? t("ui.settings.rate_verify.verifying", { defaultValue: "VERIFYING…" }) : t("ui.settings.rate_verify.run", { defaultValue: "RUN VERIFICATION" })}
          </button>
        </div>
      </div>
      {err && (
        <div
          data-testid="rate-verify-error"
          className="text-[11px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 border border-rose-700/40 px-3 py-2 rounded-xl"
        >
          {err}
        </div>
      )}
      {result?.body && (
        <div data-testid="rate-verify-result" className="space-y-3">
          <div className={`text-xs ${headerCls}`}>
            {total === 0 && pendingAcks === 0 ? (
              <span data-testid="rate-verify-summary-clean">
                ✓ {t("ui.settings.rate_verify.clean", { count: result.body.verification.length, defaultValue: `Every bootstrap matches TEDB (${result.body.verification.length} form(s) checked).` })}
              </span>
            ) : (
              <span data-testid="rate-verify-summary-drift">
                ⚠ {total === 1 ? t("ui.settings.rate_verify.discrepancy", { count: total, defaultValue: `${total} discrepancy` }) : t("ui.settings.rate_verify.discrepancies", { count: total, defaultValue: `${total} discrepancies` })}
                {pendingAcks > 0 ? t("ui.settings.rate_verify.acks_opened", { count: pendingAcks, defaultValue: ` · ${pendingAcks} backdated-rate ack(s) opened` }) : ""}
              </span>
            )}
            <span className="text-slate-500 dark:text-slate-400 ml-2">
              fetch_id: {result.body.tedb_fetch.fetch_id}
            </span>
          </div>
          {result.body.verification.map((v) => (
            <FormRow key={v.country} v={v} />
          ))}
          {pendingAcks > 0 && (
            // v0.6.1 — points at RatePendingAcksPanel, mounted right below
            // this panel in the same Settings → Rates tab (was previously
            // "/admin/amendments", a different, later-stage page that only
            // ever shows an ack AFTER it's been resolved as amendment-
            // required — there was no page for the ack itself).
            <a
              href="#rate-pending-acks"
              data-testid="rate-verify-acks-deeplink"
              className="text-[11px] tracking-normal text-amber-700 dark:text-amber-300 hover:text-amber-700 underline decoration-dotted"
            >
              {t("ui.settings.rate_verify.resolve_acks", { count: pendingAcks, defaultValue: `Resolve ${pendingAcks} backdated-rate ack(s) →` })}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const FormRow = ({ v }) => { const { t } = useTranslation(); return (
  <div
    data-testid={`rate-verify-form-${v.country}`}
    className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
  >
    <div className="bg-white dark:bg-slate-900 px-3 py-1.5 flex items-center justify-between">
      <span className="tabular-nums text-xs text-teal-700 dark:text-teal-300">
        {v.country} · {v.form_code}
      </span>
      {!v.tedb_covered && (
        <span
          data-testid={`rate-verify-uk-note-${v.country}`}
          className="text-[10px] text-slate-500 dark:text-slate-400"
          title={v.note}
        >
          {t("ui.settings.rate_verify.not_in_tedb", { defaultValue: "not in TEDB (bootstrap-only)" })}
        </span>
      )}
      {v.tedb_covered && (
        <span className={`text-[10px] ${v.any_discrepancy ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`}>
          {v.any_discrepancy ? t("ui.settings.rate_verify.drift", { defaultValue: "DRIFT" }) : t("ui.settings.rate_verify.match", { defaultValue: "MATCH" })}
        </span>
      )}
    </div>
    {v.categories.map((c) => (
      <div
        key={c.category}
        data-testid={`rate-verify-cat-${v.country}-${c.category}`}
        className="grid grid-cols-12 gap-2 px-3 py-1.5 border-t border-slate-200 dark:border-slate-700 tabular-nums text-[11px]"
      >
        <span className="col-span-3 text-slate-500 dark:text-slate-400">{c.category}</span>
        <span className="col-span-3 text-slate-600 dark:text-slate-300">
          bootstrap: <span className="text-slate-900 dark:text-slate-50">{c.bootstrap_rate}</span>
        </span>
        <span className="col-span-3 text-slate-600 dark:text-slate-300">
          history: <span className="text-slate-900 dark:text-slate-50">{c.history_rate ?? "—"}</span>
        </span>
        <span className="col-span-2 text-slate-500 dark:text-slate-400 truncate" title={c.valid_from || ""}>
          {c.valid_from || "—"}
        </span>
        <span className={`col-span-1 text-right ${c.discrepancy ? "text-amber-700 dark:text-amber-300" : c.tedb_origin ? "text-emerald-700 dark:text-emerald-300" : "text-slate-600 dark:text-slate-300"}`}>
          {c.discrepancy ? "⚠" : c.tedb_origin ? "✓" : "○"}
        </span>
      </div>
    ))}
  </div>
); };

export default RateVerifyPanel;

// v0.4.53 — Continuous-confidence badge above the RUN button.
// Reads the latest persisted drift-run row (operator OR scheduler-
// triggered). Tone:
//   - CLEAN       → emerald ✓
//   - DRIFT       → amber ⚠
//   - TEDB_UNREACHABLE → rose ⚠
//   - null (no runs yet) → slate "never run".
const LastDriftBadge = ({ run }) => {
  const { t } = useTranslation();
  if (!run) {
    return (
      <span
        data-testid="rate-verify-last-run-badge"
        className="text-[10px] text-slate-500 dark:text-slate-400"
      >
        {t("ui.settings.rate_verify.last_never", { defaultValue: "Last drift check: never" })}
      </span>
    );
  }
  const tone = run.outcome === "CLEAN"
    ? "text-emerald-700 dark:text-emerald-300"
    : run.outcome === "DRIFT" ? "text-amber-700 dark:text-amber-300" : "text-rose-700 dark:text-rose-300";
  const icon = run.outcome === "CLEAN"
    ? "✓" : run.outcome === "DRIFT" ? "⚠" : "⚠";
  const stamp = (run.finished_at || run.started_at || "").slice(0, 16).replace("T", " ");
  const detail = run.outcome === "CLEAN"
    ? t("ui.settings.rate_verify.detail_clean", { defaultValue: "clean" })
    : run.outcome === "DRIFT"
      ? t("ui.settings.rate_verify.detail_drift", { drift: run.total_discrepancies, acks: run.pending_acks_created, defaultValue: `${run.total_discrepancies} drift · ${run.pending_acks_created} ack(s)` })
      : t("ui.settings.rate_verify.detail_unreachable", { defaultValue: "TEDB unreachable" });
  const source = run.triggered_by === "scheduler" ? t("ui.settings.rate_verify.source_scheduler", { defaultValue: "scheduler" }) : t("ui.settings.rate_verify.source_manual", { defaultValue: "manual" });
  return (
    <span
      data-testid="rate-verify-last-run-badge"
      className={`tabular-nums text-[10px] ${tone}`}
      title={`run_id: ${run.id} · triggered_by: ${run.triggered_by}`}
    >
      {icon} {t("ui.settings.rate_verify.last_run", { stamp, detail, source, defaultValue: `Last drift check: ${stamp} (${detail}) · ${source}` })}
    </span>
  );
};
